import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Cloud, Sun, CloudRain, CloudLightning, CloudSnow, Wind,
  Thermometer, TrendingUp, TrendingDown, AlertTriangle,
  Calendar, RefreshCw, Bell
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CostWeather = () => {
  const { shouldShowData } = useDemoData();

  const [forecast, setForecast] = useState([]);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    fetchWeatherData();
  }, []);

  const fetchWeatherData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Get dashboard summary to check if user has cost data
      const summaryRes = await fetch(`${API_URL}/api/dashboard/summary`, { headers });
      const summary = await summaryRes.json();
      
      // Get forecast data
      const forecastRes = await fetch(`${API_URL}/api/forecast`, { headers });
      const forecastData = await forecastRes.json();
      
      const totalCost = summary?.total_cost_mtd || 0;
      
      if (totalCost === 0 && (!forecastData || forecastData.length === 0)) {
        setHasData(false);
        setLoading(false);
        return;
      }
      
      setHasData(true);
      
      // Build weather from real data
      const avgCost = totalCost > 0 ? totalCost / 30 : 0;
      const todayCost = forecastData.length > 0 ? forecastData[0]?.predicted || avgCost : avgCost;
      const yesterdayCost = avgCost;
      const variance = avgCost > 0 ? ((todayCost - avgCost) / avgCost) * 100 : 0;
      
      setCurrentWeather({
        cost: todayCost,
        change: todayCost - yesterdayCost,
        changePercent: yesterdayCost > 0 ? ((todayCost - yesterdayCost) / yesterdayCost) * 100 : 0,
        condition: getWeatherCondition(variance),
        temperature: Math.round(50 + variance),
        humidity: Math.round(75),
        wind: Math.round(Math.abs(variance))
      });
      
      // Build 7-day forecast from real forecast data
      const days = ['Today', 'Tomorrow', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const forecastItems = days.map((day, i) => {
        const item = forecastData[i] || {};
        const cost = item.predicted || avgCost;
        const v = avgCost > 0 ? ((cost - avgCost) / avgCost) * 100 : 0;
        return {
          day,
          cost: Math.round(cost),
          condition: getWeatherCondition(v),
          high: Math.round(item.upper_bound || cost * 1.1),
          low: Math.round(item.lower_bound || cost * 0.9),
          confidence: 95 - (i * 5)
        };
      });
      
      setForecast(forecastItems);
      setAlerts([]);
    } catch (error) {
      console.error('Failed to fetch weather data:', error);
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const getWeatherCondition = (variance) => {
    if (variance < -20) return { icon: Sun, label: 'Sunny', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', desc: 'Well below budget' };
    if (variance < -5) return { icon: Cloud, label: 'Partly Cloudy', color: 'text-blue-400', bg: 'bg-emerald-50 dark:bg-blue-900/20', desc: 'Under budget' };
    if (variance < 5) return { icon: Cloud, label: 'Cloudy', color: 'text-slate-400', bg: 'bg-slate-50 dark:bg-slate-800/50', desc: 'On track' };
    if (variance < 20) return { icon: CloudRain, label: 'Rainy', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-blue-900/20', desc: 'Above budget' };
    if (variance < 40) return { icon: CloudLightning, label: 'Stormy', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-cyan-900/20', desc: 'Significantly over' };
    return { icon: CloudSnow, label: 'Blizzard', color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-900/20', desc: 'Critical overspend' };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Cost Weather" description="Connect your cloud account to see real data here." />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Cloud className="w-12 h-12 text-emerald-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400">Generating cost forecast...</p>
        </div>
      </div>
    );
  }

  if (!hasData || !currentWeather) {
    return (
      <div className="space-y-6" data-testid="cost-weather-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Cloud className="h-7 w-7 text-emerald-500" />
            Cost Weather
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            7-day cost forecast with weather-style visualization
          </p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Cloud className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">No Cost Data Available</h3>
            <p className="text-slate-400 text-center max-w-md">Connect your cloud providers to see cost weather forecasts and spending predictions.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const WeatherIcon = currentWeather.condition.icon;

  return (
    <div className="space-y-6" data-testid="cost-weather-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Cloud className="h-7 w-7 text-emerald-500" />
            Cost Weather
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            7-day cost forecast with weather-style visualization
          </p>
        </div>
        <Button variant="outline" onClick={fetchWeatherData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Forecast
        </Button>
      </div>

      {/* Current Weather Card */}
      <Card className={`${currentWeather.condition.bg} border-0`}>
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <div className="text-center">
                <WeatherIcon className={`w-24 h-24 ${currentWeather.condition.color}`} />
                <p className="text-lg font-medium mt-2">{currentWeather.condition.label}</p>
                <p className="text-sm text-slate-400">{currentWeather.condition.desc}</p>
              </div>
              
              <div>
                <p className="text-6xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(currentWeather.cost)}
                </p>
                <p className="text-xl text-slate-400 mt-1">Today's Spending</p>
                <div className={`flex items-center gap-1 mt-2 ${
                  currentWeather.change > 0 ? 'text-rose-600' : 'text-emerald-600'
                }`}>
                  {currentWeather.change > 0 ? (
                    <TrendingUp className="w-5 h-5" />
                  ) : (
                    <TrendingDown className="w-5 h-5" />
                  )}
                  <span className="font-medium">
                    {currentWeather.change > 0 ? '+' : ''}{formatCurrency(currentWeather.change)} ({currentWeather.changePercent.toFixed(1)}%)
                  </span>
                  <span className="text-slate-400 ml-1">vs yesterday</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <Thermometer className="w-6 h-6 text-rose-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{currentWeather.temperature}°</p>
                <p className="text-xs text-slate-400">Cost Index</p>
              </div>
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <Cloud className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{currentWeather.humidity}%</p>
                <p className="text-xs text-slate-400">Utilization</p>
              </div>
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <Wind className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                <p className="text-2xl font-bold">{currentWeather.wind}</p>
                <p className="text-xs text-slate-400">Volatility</p>
              </div>
              <div className="text-center p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <Bell className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{alerts.length}</p>
                <p className="text-xs text-slate-400">Alerts</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 7-Day Forecast */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            7-Day Cost Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-4 mb-6">
            {forecast.map((day, idx) => {
              const DayIcon = day.condition.icon;
              return (
                <div 
                  key={day.day}
                  className={`text-center p-4 rounded-xl transition-all hover:scale-105 ${
                    idx === 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-2 ring-emerald-500' : 'bg-slate-50 dark:bg-slate-800/50'
                  }`}
                >
                  <p className={`text-sm font-medium mb-2 ${idx === 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                    {day.day}
                  </p>
                  <DayIcon className={`w-10 h-10 mx-auto ${day.condition.color}`} />
                  <p className="text-lg font-bold mt-2">{formatCurrency(day.cost)}</p>
                  <p className="text-xs text-slate-400 mt-1">{day.confidence}% conf.</p>
                </div>
              );
            })}
          </div>

          {/* Forecast Chart */}
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={forecast}>
              <defs>
                <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="day" />
              <YAxis tickFormatter={(v) => `$${v/1000}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Area 
                type="monotone" 
                dataKey="high" 
                stroke="#F59E0B" 
                fill="none" 
                strokeDasharray="5 5"
                name="High Estimate"
              />
              <Area 
                type="monotone" 
                dataKey="cost" 
                stroke="#10B981" 
                fill="url(#forecastGradient)" 
                strokeWidth={3}
                name="Forecast"
              />
              <Area 
                type="monotone" 
                dataKey="low" 
                stroke="#3B82F6" 
                fill="none" 
                strokeDasharray="5 5"
                name="Low Estimate"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Weather Alerts */}
      <div className="grid md:grid-cols-2 gap-4">
        {alerts.map((alert) => (
          <Card 
            key={alert.id} 
            className={`border-l-4 ${
              alert.severity === 'high' 
                ? 'border-l-rose-500 bg-rose-50 dark:bg-rose-900/20' 
                : 'border-l-blue-500 bg-emerald-50 dark:bg-blue-900/20'
            }`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {alert.type === 'storm' ? (
                  <CloudLightning className="w-6 h-6 text-rose-500 flex-shrink-0" />
                ) : (
                  <Sun className="w-6 h-6 text-amber-500 flex-shrink-0" />
                )}
                <div>
                  <h4 className="font-semibold">{alert.title}</h4>
                  <p className="text-sm text-slate-400 dark:text-slate-400 mt-1">{alert.message}</p>
                  <Badge className="mt-2" variant="secondary">{alert.day}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weather Legend */}
      <Card className="bg-slate-50 dark:bg-slate-800/50">
        <CardContent className="p-4">
          <p className="text-sm font-medium mb-3">Weather Legend</p>
          <div className="flex flex-wrap gap-4">
            {[
              { icon: Sun, label: 'Sunny (Under budget)', color: 'text-amber-500' },
              { icon: Cloud, label: 'Cloudy (On track)', color: 'text-slate-400' },
              { icon: CloudRain, label: 'Rainy (Over budget)', color: 'text-emerald-600' },
              { icon: CloudLightning, label: 'Stormy (Significantly over)', color: 'text-emerald-600' },
              { icon: CloudSnow, label: 'Blizzard (Critical)', color: 'text-rose-600' }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <item.icon className={`w-5 h-5 ${item.color}`} />
                <span className="text-sm text-slate-400 dark:text-slate-400">{item.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostWeather;
